// /interactions — visual drug interaction graph. Loads the user's
// medications and most recent flag run, renders the graph, then
// lists the underlying flags below the graph.

import type { Flag, Medication } from '@medreview/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMedications } from '../api/medications';
import { ComplianceNotice } from '../components/ComplianceNotice';
import { InteractionGraph } from '../components/InteractionGraph';
import { ReadAloud } from '../components/ReadAloud';
import { ApiError } from '../components/senior/ApiError';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { SkeletonInteractionGraph } from '../components/senior/Skeleton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useSubjectName } from '../hooks/useSubjectName';
import { AppPage } from '../layouts/AppPage';

const SEVERITY_LABEL: Record<Flag['severity'], string> = {
  CRITICAL: 'Critical',
  MAJOR: 'Major',
  MODERATE: 'Moderate',
  MINOR: 'Minor',
};

const SEVERITY_CLASS: Record<Flag['severity'], string> = {
  CRITICAL: 'food-pill--avoid',
  MAJOR: 'food-pill--avoid',
  MODERATE: 'food-pill--limit',
  MINOR: 'food-pill--note',
};

// Default-on severity filter per Dr. Marlee Grounds' UAT note: most
// users do not need MINOR/MODERATE chatter, only the actionable
// CRITICAL + MAJOR. Toggle exposes the full list when the user wants
// it.
type SeverityFilter = 'major-plus' | 'all';

export function Interactions(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const subject = useSubjectName();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SeverityFilter>('major-plus');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [medList, flagsRes] = await Promise.all([
          listMedications(authedFetch),
          authedFetch('/api/flags'),
        ]);
        if (cancelled) return;
        setMeds(medList);
        const json = (await flagsRes.json()) as {
          ok: boolean;
          data?: { flags: Flag[] };
        };
        if (json.ok && json.data) setFlags(json.data.flags);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  const idToName = new Map(meds.map((m) => [m.id, m.name]));

  const visibleFlags =
    filter === 'all'
      ? flags
      : flags.filter((f) => f.severity === 'CRITICAL' || f.severity === 'MAJOR');
  const hiddenCount = flags.length - visibleFlags.length;

  return (
    <AppPage
      title={subject.possessive ? `${subject.possessive} interactions` : 'Interactions'}
      back={{ to: '/medications', label: 'Medicines' }}
    >
      <ComplianceNotice
        variant="clinical"
        detail="Flags come from public drug safety lists. Check with your pharmacist before you change a dose."
      />
      {loading ? <SkeletonInteractionGraph /> : null}
      {error ? (
        <ApiError
          title="Could not build your interaction map"
          error={error}
          onRetry={() => window.location.reload()}
        />
      ) : null}

      {!loading && meds.length === 0 ? (
        <EmptyIllustration
          kind="interactions"
          title="No medicines yet"
          body="The interaction graph shows how your medicines relate to each other. Add at least one to get started."
          cta={
            <Link to="/medications/add-by-photo" className="home-hero__cta">
              Add a medicine
            </Link>
          }
        />
      ) : null}

      {!loading && meds.length > 0 ? (
        <>
          <section className="account-section" aria-label="Plain-text summary">
            <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{graphSummary(flags)}</p>
            {flags.length > 0 ? (
              <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', margin: '6px 0 0' }}>
                Tap a row below to read the details. The graph is the same information in a picture.
              </p>
            ) : null}
          </section>

          <section
            className="account-section"
            style={{ display: 'flex', justifyContent: 'center' }}
            aria-label="Interactions graph"
          >
            <InteractionGraph medications={meds} flags={flags} size={400} />
          </section>

          <section className="account-section">
            <h3 className="account-section__title">Legend</h3>
            <div className="legend-row">
              <span className="legend-swatch" style={{ background: '#b91c1c' }} />
              <span>Critical</span>
              <span className="legend-swatch" style={{ background: '#c2410c' }} />
              <span>Major</span>
              <span className="legend-swatch" style={{ background: '#a16207' }} />
              <span>Moderate</span>
              <span className="legend-swatch" style={{ background: '#475569' }} />
              <span>Minor</span>
            </div>
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', marginTop: 12 }}>
              Lines between medicines mean two medicines work together. A ring around one medicine
              means a flag about that one medicine.
            </p>
          </section>

          {flags.length > 0 ? (
            <section className="account-section">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                }}
              >
                <h3 className="account-section__title" style={{ margin: 0 }}>
                  Flags ({visibleFlags.length}
                  {hiddenCount > 0 ? ` of ${flags.length}` : ''})
                </h3>
                <div
                  role="tablist"
                  aria-label="Severity filter"
                  style={{ display: 'inline-flex', gap: 6 }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={filter === 'major-plus'}
                    className={`symptom-pill${filter === 'major-plus' ? ' is-active' : ''}`}
                    style={{ minWidth: 92 }}
                    onClick={() => setFilter('major-plus')}
                  >
                    Major +
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={filter === 'all'}
                    className={`symptom-pill${filter === 'all' ? ' is-active' : ''}`}
                    style={{ minWidth: 72 }}
                    onClick={() => setFilter('all')}
                  >
                    All
                  </button>
                </div>
              </div>
              <ReadAloud label="Listen to my flags" text={flagsSpoken(visibleFlags, idToName)} />
              {filter === 'major-plus' && hiddenCount > 0 ? (
                <p
                  style={{
                    fontSize: 14,
                    color: 'var(--color-text-secondary)',
                    margin: '0 0 12px',
                  }}
                >
                  {hiddenCount} moderate / minor flag{hiddenCount === 1 ? '' : 's'} hidden. Tap{' '}
                  <strong>All</strong> to see them.
                </p>
              ) : null}
              <ul className="food-list">
                {visibleFlags.map((f) => (
                  <li key={f.id} className="food-item">
                    <span className={`food-pill ${SEVERITY_CLASS[f.severity]}`}>
                      {SEVERITY_LABEL[f.severity]}
                    </span>
                    <div>
                      <p className="food-item__name">{f.message}</p>
                      <p className="food-item__reason">
                        {f.explanationGrade6 ?? f.rationale}
                        {f.medicationIds.length > 0 ? (
                          <span style={{ display: 'block', marginTop: 4, fontWeight: 600 }}>
                            {f.medicationIds
                              .map((id) => idToName.get(id) ?? '?')
                              .filter(Boolean)
                              .join(' + ')}
                          </span>
                        ) : null}
                        {f.source === 'ACB' && typeof f.acbScore === 'number' ? (
                          <span
                            style={{
                              display: 'block',
                              marginTop: 8,
                              padding: 10,
                              background: 'var(--color-surface-secondary)',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 14,
                            }}
                          >
                            <strong>Your number: {f.acbScore} of 12.</strong> The average for adults
                            65+ is 2. Anything 3 or higher is worth a conversation with your
                            pharmacist.
                            <span
                              style={{
                                display: 'block',
                                marginTop: 6,
                                fontSize: 13,
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              Contributing medicines:{' '}
                              {f.medicationIds
                                .map((id) => idToName.get(id))
                                .filter(Boolean)
                                .join(', ')}
                              . To push your score lower, ask about non-Benadryl sleep options or a
                              non-anticholinergic alternative for any of these.
                            </span>
                          </span>
                        ) : null}
                        {f.evidenceCitations && f.evidenceCitations.length > 0 ? (
                          <CitationDisclosure
                            shortLabel={shortCitation(f.evidenceCitations[0] ?? '')}
                            fullText={f.evidenceCitations[0] ?? ''}
                          />
                        ) : null}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <div className="empty-state" style={{ paddingTop: 32, paddingBottom: 32 }}>
              <h3 className="empty-state__title">No flags on your current list</h3>
              <p className="empty-state__body">
                Siggy did not see any safety flags on your list. Keep your list up to date as things
                change.
              </p>
            </div>
          )}

          <section
            className="account-section"
            style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}
          >
            <p>Each flag links to its source. Reviewed by Dr. Marlee Grounds, PharmD.</p>
          </section>
        </>
      ) : null}
    </AppPage>
  );
}

// Source: line that defaults to a short label and reveals the full
// DOI/license blob on tap. Focus group #14 (5/16): the rigorous tier
// loves citations, the rest find them noisy. This satisfies both.
function CitationDisclosure({
  shortLabel,
  fullText,
}: {
  shortLabel: string;
  fullText: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{
        display: 'block',
        marginTop: 8,
        fontSize: 13,
        color: 'var(--color-text-tertiary)',
      }}
    >
      Source: {shortLabel}{' '}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          background: 'transparent',
          border: 0,
          color: 'var(--color-accent)',
          cursor: 'pointer',
          fontSize: 13,
          textDecoration: 'underline',
          padding: 0,
        }}
      >
        {open ? 'hide full citation' : 'show full citation'}
      </button>
      {open ? (
        <span style={{ display: 'block', marginTop: 4, lineHeight: 1.5 }}>{fullText}</span>
      ) : null}
    </span>
  );
}

// Short label for a citation: take everything before the first period
// or up to 80 characters, whichever comes first. Good enough for the
// "Source: STOPP/START v3 (2023)" style preview without exposing the
// DOI and license blob.
function shortCitation(full: string): string {
  const beforePeriod = full.split('.')[0] ?? full;
  return beforePeriod.length > 80 ? `${beforePeriod.slice(0, 77)}...` : beforePeriod;
}

function graphSummary(flags: Flag[]): string {
  if (flags.length === 0) return 'No safety flags on your current list.';
  const counts = { critical: 0, major: 0, moderate: 0, minor: 0 };
  for (const f of flags) {
    if (f.severity === 'CRITICAL') counts.critical++;
    else if (f.severity === 'MAJOR') counts.major++;
    else if (f.severity === 'MODERATE') counts.moderate++;
    else counts.minor++;
  }
  const parts: string[] = [];
  if (counts.critical > 0) parts.push(`${counts.critical} critical`);
  if (counts.major > 0) parts.push(`${counts.major} major`);
  if (counts.moderate > 0) parts.push(`${counts.moderate} moderate`);
  if (counts.minor > 0) parts.push(`${counts.minor} minor`);
  return `You have ${parts.join(', ')} flag${flags.length === 1 ? '' : 's'} to look at.`;
}

function flagsSpoken(flags: Flag[], idToName: Map<string, string>): string {
  if (flags.length === 0) return 'No flags on your current list.';
  const intro = `You have ${flags.length} flag${flags.length === 1 ? '' : 's'} to look at.`;
  const sentences = flags.map((f) => {
    const meds = f.medicationIds
      .map((id) => idToName.get(id))
      .filter(Boolean)
      .join(' and ');
    const lead = `${SEVERITY_LABEL[f.severity]}: ${f.message}.`;
    const explanation = f.explanationGrade6 ?? f.rationale;
    return meds ? `${lead} For ${meds}. ${explanation}` : `${lead} ${explanation}`;
  });
  return `${intro} ${sentences.join(' ')}`;
}
